import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ClipDetail, CopySaved } from '@2x3/shared';
import CopyForm from '../components/CopyForm.js';
import Player from '../components/Player.js';
import { api } from '../lib/api.js';
import { formatDuration, plural, utcStamp } from '../lib/format.js';
import { useSession } from '../lib/session.js';

export default function ClipPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { me, loading: sessionLoading, refresh } = useSession();
  const [clip, setClip] = useState<ClipDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<CopySaved | null>(null);
  const [nextBusy, setNextBusy] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const listened = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setClip(null);
    setError(null);
    setSaved(null);
    setNoMore(false);
    listened.current = 0;
    api.clip(id).then(
      (c) => {
        if (!cancelled) setClip(c);
      },
      (err: Error) => {
        if (!cancelled) setError(err.message);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [id, me?.callsign]);

  const next = async (): Promise<void> => {
    setNextBusy(true);
    try {
      const { id: nextId } = await api.next(id);
      if (nextId) navigate(`/clip/${nextId}`);
      else setNoMore(true);
    } finally {
      setNextBusy(false);
    }
  };

  const onSaved = (s: CopySaved): void => {
    setSaved(s);
    void refresh();
    api.clip(id).then(setClip, () => undefined);
  };

  if (error) {
    return (
      <div className="clip-page">
        <p className="alert is-error">{error}</p>
        <Link to="/clips">All clips</Link>
      </div>
    );
  }
  if (!clip) return <p className="muted">Loading...</p>;

  return (
    <div className="clip-page">
      <div className="clip-head">
        <h1>
          Clip <span className="mono">{clip.id}</span>
        </h1>
        <p className="clip-meta">
          <span>{clip.band}</span>
          <span>
            {clip.date} <span className="muted">{clip.hours}</span>
          </span>
          <span>{formatDuration(clip.durationSec)}</span>
          {clip.wpmEst ? <span>~{clip.wpmEst} wpm</span> : null}
          <span className="muted">
            {clip.copies} of {clip.targetCopies} copies
          </span>
        </p>
        <p className="muted small">
          Copy the station on the centre tone (about 600 Hz), both sides of the contact if it's a QSO. Stations off to
          the side at a clearly different pitch are someone else's clip.
        </p>
      </div>

      {/* Said before anyone presses play: this one is done. */}
      {me && clip.myCopy && !saved && (
        <div className="alert is-good copied-banner" role="status">
          <span>
            <strong>&#10003; You've copied this clip</strong>
            <span className="muted"> - saved {utcStamp(clip.myCopy.updatedAt)}.</span> No need to copy it again; your
            copy is below if you want to correct something.
          </span>
          <button type="button" className="btn-primary" onClick={() => void next()} disabled={nextBusy}>
            {nextBusy ? 'Finding...' : 'Next clip →'}
          </button>
        </div>
      )}

      <Player src={clip.audioUrl} listened={listened} />

      {saved && (
        <div className="alert is-good saved-banner" role="status">
          <span>
            Saved{saved.revision > 1 ? ` (revision ${saved.revision})` : ''}. This clip now has{' '}
            {plural(saved.copies, 'copy', 'copies')} of the {saved.targetCopies} it needs.
          </span>
          <button type="button" className="btn-primary" onClick={() => void next()} disabled={nextBusy}>
            {nextBusy ? 'Finding...' : 'Next clip →'}
          </button>
        </div>
      )}
      {noMore && <p className="alert">You've copied every open clip. Thank you!</p>}

      {me ? (
        <CopyForm key={`${clip.id}:${me.callsign}`} clip={clip} listened={listened} onSaved={onSaved} />
      ) : sessionLoading ? null : (
        <div className="invite-note">
          <h2>Copying is by invitation</h2>
          <p>
            Anyone can listen. To send in a copy, open the invite link WB5SKM sent you, on this device. It signs you in
            as your callsign, so your copy is credited to you.
          </p>
          <p className="muted small">
            Why invitations? Copies become the references a decoder is scored against, so each one needs a named
            operator behind it.
          </p>
        </div>
      )}
    </div>
  );
}
